import {
	Anchor,
	Box,
	Card,
	Flex,
	Group,
	Stack,
	useMantineTheme,
} from '@mantine/core';
import type { AiChatRatedMessageItem } from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { Selector, SelectorTarget } from '@repo/common/components/Selectors';
import { SelectType } from '@repo/common/components/Selectors/types';
import { UserAvatar } from '@repo/common/components/UserAvatar';
import { Icon, Text } from '@repo/foundations';
import { memo, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { Virtuoso } from 'react-virtuoso';
import { useAuthUser } from '../../../api';
import { SentimentIcon } from '../../../pages/AiChatsPage/components/SentimentIcon';
import { AI_IMPACT_DASHBOARD_WIDGETS_HEIGHT, SORT_OPTIONS } from '../constants';
import type {
	AiChatRatedMessagesComponentProps,
	AiChatRatedMessagesContentProps,
	AiChatRatedMessagesSortOrder,
} from '../types';
import { formatRelativeTime } from '../utils';
import { AiImpactWidgetEmpty } from './AiImpactWidgetEmpty';
import { AiImpactWidgetSkeleton } from './AiImpactWidgetSkeleton';
import { useStyles } from './styles';

const AiChatRatedMessagesContent = memo(
	({
		items,
		sortOrder,
		onSortChange,
		theme,
		hasNextPage,
		isFetching,
		onLoadMore,
		error,
		dateFilter,
	}: AiChatRatedMessagesContentProps) => {
		const navigate = useNavigate();
		const { classes } = useStyles();
		const { user, workspace } = useAuthUser();

		const handleSortChange = useCallback(
			(values: string[]) => {
				const newSortOrder = values[0] as AiChatRatedMessagesSortOrder;
				if (newSortOrder && newSortOrder !== sortOrder) {
					onSortChange(newSortOrder);
				}
			},
			[onSortChange, sortOrder]
		);

		const handleChatClick = useCallback(
			(chat: AiChatRatedMessageItem) => {
				trackEvent(
					'ai_impact_dashboard/chat_row_clicked',
					{
						chat_id: chat.id,
						quality_score: '0',
						widget_type: 'rated_messages',
						sort_order: sortOrder,
						current_date_filter: dateFilter ?? '30d',
					},
					user,
					workspace
				);

				const filter = JSON.stringify([
					{
						value: [chat.sentiment],
						operator: 'is',
						filterType: 'sentiment',
					},
				]);
				navigate(`/analytics/ai-chats?filters=${encodeURIComponent(filter)}`);
			},
			[navigate, sortOrder, dateFilter, user, workspace]
		);

		const itemContent = useCallback(
			(index: number, chat: AiChatRatedMessageItem) => (
				<Box
					className={classes.card}
					pb="md"
					onClick={() => handleChatClick(chat)}
				>
					<Flex justify="space-between" align="center">
						<Flex
							justify="start"
							align="center"
							gap="sm"
							className={classes.avatarInfo}
						>
							<UserAvatar
								size="md"
								user={{
									id: chat.user_id,
									display_name: chat.user_name || 'Unknown user',
									first_name: '',
									last_name: '',
									profile_picture: (
										chat as typeof chat & {
											user_profile_picture?: string;
										}
									).user_profile_picture,
								}}
							/>
							<Stack spacing={0} className={classes.avatarInfo}>
								<Text
									size="sm"
									color="text/primary/default"
									weight="semibold"
									lineClamp={1}
								>
									{chat.title || chat.prompt}
								</Text>
								<Text size="xs" color="text/secondary/default" lineClamp={1}>
									{`Asked by ${chat.user_name || 'Unknown user'} · ${formatRelativeTime(chat.created_at)}`}
								</Text>
							</Stack>
						</Flex>
						<Box style={{ flexShrink: 0 }}>
							<SentimentIcon sentiment={chat.sentiment} />
						</Box>
					</Flex>
				</Box>
			),
			[classes.card, classes.avatarInfo, handleChatClick]
		);

		if ((items.length === 0 && !isFetching) || error) {
			return (
				<AiImpactWidgetEmpty
					theme={theme}
					message={
						error
							? 'Error loading rated messages'
							: 'No rated messages found for the selected time period'
					}
					icon={error ? 'exclamationCircle' : 'messageCircle'}
				/>
			);
		}

		return (
			<Stack spacing={0}>
				<Group position="apart" pt="sm" pr="sm" pb="sm" pl="md">
					<Group>
						<Icon name="messageCircle" size="md" color="text/primary/default" />
						<Text size="sm" color="text/primary/default" fw={600}>
							Rated messages
						</Text>
					</Group>
					<Selector
						selectType={SelectType.Single}
						initialValue={[sortOrder]}
						onChange={handleSortChange}
						getItems={async () => SORT_OPTIONS}
						disableSearch
					>
						<SelectorTarget size="md" variant="default">
							{SORT_OPTIONS.find((opt) => opt.value === sortOrder)?.label}
						</SelectorTarget>
					</Selector>
				</Group>
				<Box
					mih={AI_IMPACT_DASHBOARD_WIDGETS_HEIGHT}
					h={AI_IMPACT_DASHBOARD_WIDGETS_HEIGHT}
				>
					<Virtuoso
						style={{ height: '100%' }}
						data={items}
						endReached={() => {
							if (hasNextPage && !isFetching) {
								onLoadMore();
							}
						}}
						itemContent={itemContent}
					/>
				</Box>
			</Stack>
		);
	}
);

export const AiChatRatedMessages = memo(
	({
		items,
		onSortChange,
		sortOrder,
		isLoading,
		error,
		handleSeeAllClick,
		hasNextPage,
		isFetching,
		onLoadMore,
		dateFilter,
	}: AiChatRatedMessagesComponentProps) => {
		const theme = useMantineTheme();

		if (isLoading && items.length === 0) {
			return <AiImpactWidgetSkeleton halfWidth />;
		}

		return (
			<Card
				p={theme.spacing.xs}
				withBorder
				w="50%"
				style={{ borderRadius: theme.radius.md }}
			>
				<Stack>
					<AiChatRatedMessagesContent
						items={items}
						sortOrder={sortOrder}
						onSortChange={onSortChange}
						theme={theme}
						hasNextPage={hasNextPage}
						isFetching={isFetching}
						onLoadMore={onLoadMore}
						error={error}
						dateFilter={dateFilter}
					/>
					<Flex justify="flex-end" pb="sm">
						<Anchor
							color={theme.other.getColor('text/emphasis/default')}
							weight="semibold"
							fz="sm"
							onClick={handleSeeAllClick}
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: theme.spacing['2xs'],
							}}
						>
							<Text size="sm" color="text/emphasis/default">
								View more
							</Text>
							<Icon name="chevronRight" color="text/emphasis/default" />
						</Anchor>
					</Flex>
				</Stack>
			</Card>
		);
	}
);
